import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, CardBody, Col, Container, Row,
  Form, FormGroup, Label, Input, Button, CardHeader, Spinner
} from 'reactstrap';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useHistory, useParams } from "react-router-dom";
import Cropper from 'react-easy-crop';
import PlanSelectionModal from "../Components/PlanSelectionModal";
import { getDealCroppedImg } from '../../Components/Common/DealCrop';
import { getSubscription } from "../BaseUrl/SubscriptionApi";

import TimePicker from "rc-time-picker";
import moment from "moment";
import "rc-time-picker/assets/index.css";

function UpdateHH() {
  const history = useHistory();
  const { dealId } = useParams();

  const [formData, setFormData] = useState({
    categoryId: '',
    days: [],
    startTime: '',
    endTime: '',
    dealPhoto: [],
    removePhotoIds: []
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [dealLoading, setDealLoading] = useState(true);
  const [selectedFileName, setSelectedFileName] = useState('No file chosen');
  const fileInputRef = useRef(null);
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const [dealPhotoPreviews, setDealPhotoPreviews] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for PlanSelectionModal

  // Subscription states
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);

  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  const daysOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Determine if user is on free plan
  const isFreePlan = subscriptionLoading ? false : (!subscription || subscription.subscription === null || subscription.subscription?.planName === "free");


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}categories`);
        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.status}`);
        }
        const data = await response.json();
        if (!data.responseData?.categories) {
          throw new Error('Categories data not found in response');
        }
        setCategories(data.responseData.categories);
      } catch (error) {
        toast.error(`Failed to load categories: ${error.message}`);
      } finally {
        setCategoriesLoading(false);
      }
    };

    // Fetch Subscription
    const fetchSub = async () => {
      try {
        const res = await getSubscription();
        setSubscription(res?.responseData || null);
      } catch (err) {
        console.error("Failed to fetch subscription:", err);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchCategories();
    fetchSub();
  }, []);

  useEffect(() => {
    if (!dealId) {
      toast.error('Deal ID is missing.');
      setDealLoading(false);
      return;
    }

    const fetchDeal = async () => {
      try {
        setDealLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}business/deal/happyHours/${dealId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch deal: ${response.status}`);
        }

        const data = await response.json();
        const deal = data.responseData?.deal;

        if (!deal) {
          throw new Error('Happy Hour deal data not found in response');
        }

        const existingPhotos = [];
        if (deal.dealPhoto && Array.isArray(deal.dealPhoto)) {
          for (const photo of deal.dealPhoto) {
            if (photo.url) {
              try {
                existingPhotos.push({
                  id: photo._id,
                  url: photo.url,
                });
              } catch (error) {
                console.error('Error processing photo:', error);
              }
            }
          }
        }

        console.log('Fetched deal:', deal);

        setFormData({
          categoryId: deal.categoryId || '',
          days: deal.days || [],
          startTime: extractTimeFromISO(deal.timeRange?.startTime),
          endTime: extractTimeFromISO(deal.timeRange?.endTime),
          dealPhoto: existingPhotos,
          removePhotoIds: []
        });

        setDealPhotoPreviews(existingPhotos.map(photo => photo.url));
        setSelectedFileNames(existingPhotos.map(() => 'deal-photo.jpg'));
      } catch (error) {
        toast.error(`Failed to load happy hour deal: ${error.message}`);
      } finally {
        setDealLoading(false);
      }
    };

    fetchDeal();
  }, [dealId]);

  useEffect(() => {
    if (isFreePlan && formData.days.length >= 5) {
      setShowUpgradeBanner(true);
    } else {
      setShowUpgradeBanner(false);
    }
  }, [formData.days, isFreePlan]);


  const extractTimeFromISO = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error('Error parsing time:', error);
      return '';
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startTime' || name === 'endTime') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDayChange = (day) => {
    setFormData(prev => {
      let days = [...prev.days];
      if (days.includes(day)) {
        days = days.filter(d => d !== day);
        if (isFreePlan && days.length <= 5) {
          setShowUpgradeBanner(false);
        }
        return { ...prev, days };
      } else {
        if (isFreePlan && days.length >= 5) {
          setShowUpgradeBanner(true);
          return prev;
        }
        return { ...prev, days: [...days, day] };
      }
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setShowCropper(true);
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async () => {
    try {
      const croppedImageBlob = await getDealCroppedImg(
        imageToCrop,
        croppedAreaPixels
      );

      const fileName = 'deal-photo.jpg';
      const croppedImageFile = new File([croppedImageBlob], fileName, {
        type: 'image/jpeg',
      });

      const previewUrl = URL.createObjectURL(croppedImageBlob);

      setFormData((prev) => {
        if (prev.dealPhoto.length >= 2) {
          toast.error("You can only upload up to 2 images.");
          return prev;
        }
        return {
          ...prev,
          dealPhoto: [...prev.dealPhoto, { file: croppedImageFile, url: previewUrl }],
        };
      });

      setDealPhotoPreviews((prev) => {
        if (prev.length >= 2) return prev;
        return [...prev, previewUrl];
      });

      setSelectedFileNames((prev) => {
        if (prev.length >= 2) return prev;
        return [...prev, fileName];
      });

      setShowCropper(false);
    } catch (e) {
      toast.error('Error cropping image');
      console.error('Error cropping image', e);
    }
  };

  const handleRemovePhoto = (index, photoId) => {
    setFormData(prev => {
      const updatedPhotos = [...prev.dealPhoto];
      const removedPhoto = updatedPhotos.splice(index, 1)[0];
      const updatedRemovePhotoIds = [...prev.removePhotoIds];
      if (removedPhoto.id) {
        updatedRemovePhotoIds.push(removedPhoto.id);
      }
      return {
        ...prev,
        dealPhoto: updatedPhotos,
        removePhotoIds: updatedRemovePhotoIds
      };
    });

    setDealPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    setSelectedFileNames(prev => prev.filter((_, i) => i !== index));
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  // ✅ Check required fields
  if (
    !formData.categoryId ||
    !formData.startTime ||
    !formData.endTime ||
    formData.days.length === 0
  ) {
    toast.error("Please fill all required fields before submitting.");
    return;
  }

  // ✅ Time validation — same logic as AddHH
  const today = moment().format("YYYY-MM-DD");
  let startMoment = moment(`${today}T${formData.startTime}`);
  let endMoment = moment(`${today}T${formData.endTime}`);

  // ✅ Handle next-day rollover if endTime < startTime
  if (!endMoment.isAfter(startMoment)) {
    endMoment.add(1, "day");
  }

  // ✅ Duration should not exceed 12 hours
  const durationHours = moment.duration(endMoment.diff(startMoment)).asHours();
  if (durationHours > 12) {
    toast.error(
      `Happy Hours deal cannot exceed 12 hours. Current duration: ${Math.floor(durationHours)}h ${Math.floor(
        (durationHours % 1) * 60
      )}m`
    );
    return;
  }

  setLoading(true);
  try {
    const formDataToSend = new FormData();
    formDataToSend.append("categoryId", formData.categoryId);
    formData.days.forEach((day) => formDataToSend.append("days[]", day));

    const startISO = startMoment.format("YYYY-MM-DDTHH:mm:ss");
    const endISO = endMoment.format("YYYY-MM-DDTHH:mm:ss");

    formDataToSend.append("startTime", startISO);
    formDataToSend.append("endTime", endISO);

    // Handle uploaded images
    formData.dealPhoto.forEach((photo) => {
      if (photo.file) {
        formDataToSend.append("dealPhoto", photo.file);
      }
    });

    formData.removePhotoIds.forEach((id) =>
      formDataToSend.append("removePhotoIds[]", id)
    );

    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}business/deal/happyHours/${dealId}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: formDataToSend,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.responseMessage || "Failed to update Happy Hours deal");
    }

    const isSuccess =
      data.responseCode === 200 ||
      (Array.isArray(data.responseMessage) &&
        data.responseMessage.some((msg) =>
          msg.toLowerCase().includes("success")
        ));

    if (isSuccess) {
      toast.success("Happy Hours deal updated successfully!");
      setTimeout(() => history.push("/happy-hour-deal-list"), 1500);
    } else {
      throw new Error("Failed to update Happy Hours deal");
    }
  } catch (error) {
    console.error("Error updating Happy Hours deal:", error);
    toast.error(error.message || "Something went wrong while updating.");
  } finally {
    setLoading(false);
  }
};



  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSubscriptionSuccess = () => {
    setShowUpgradeBanner(false);
    setIsModalOpen(false);
    // Refresh subscription data
    const fetchSub = async () => {
      try {
        const res = await getSubscription();
        setSubscription(res?.responseData || null);
      } catch (err) {
        console.error("Failed to fetch subscription:", err);
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSub();
  };

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags>
          <title>Update Happy Hour</title>
        </MetaTags>
        <ToastContainer />

        {/* PlanSelectionModal for Subscription Plans */}
        <PlanSelectionModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          isUpgrade={true}
          onSubscriptionSuccess={handleSubscriptionSuccess}
        />

        <div className="mb-3 ms-4"></div>

        <Row>
          <Col lg="12">
            {!subscriptionLoading && showUpgradeBanner && isFreePlan && (
              <div className="alert alert-warning d-flex justify-content-between align-items-center mt-2">
                <span>
                  You've reached the <b>Free Plan</b> limit (5 days).
                  Upgrade to unlock unlimited selections!
                </span>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => {
                    setIsModalOpen(true);
                    localStorage.setItem("redirectAfterSubscribe", window.location.pathname);
                  }}
                >
                  {subscription === null ? "Add Card" : "Upgrade Plan"}
                </Button>
              </div>
            )}
            <Card>
              <CardHeader className='d-flex align-items-center'>
                <Link to="/happy-hour-deal-list">
                  <Button color="primary" className="btn">
                    <i className="mdi mdi-arrow-left"></i>
                  </Button>
                </Link>
                <h4 className="card-title mb-0 ms-2">Update Happy Hour</h4>
              </CardHeader>
              <CardBody>
                {dealLoading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                    <span className="ms-2">Loading happy hour data...</span>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Category <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="categoryId"
                            value={formData.categoryId}
                            onChange={handleInputChange}
                            disabled={categoriesLoading}
                            required
                          >
                            <option value="">Select a category</option>
                            {categories.map(category => (
                              <option key={category._id} value={category._id}>
                                {category.name}
                              </option>
                            ))}
                          </Input>
                          {categoriesLoading && <small className="text-muted">Loading categories...</small>}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Deal Photos</Label>
                          <div className="d-flex">
                            <Button
                              type="button"
                              color="primary"
                              onClick={() => fileInputRef.current.click()}
                              disabled={formData.dealPhoto.length >= 2}
                              style={{
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0
                              }}
                            >
                              Choose File
                            </Button>
                            <Input
                              type="text"
                              readOnly
                              value={selectedFileName}
                              style={{
                                flex: 1,
                                cursor: "pointer",
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0
                              }}
                              onClick={() => fileInputRef.current.click()}
                            />
                            <input
                              type="file"
                              name="dealPhoto"
                              onChange={handleFileChange}
                              ref={fileInputRef}
                              accept="image/*"
                              style={{ display: "none" }}
                            />
                          </div>
                          {formData.dealPhoto.length > 0 && (
                            <div className="mt-3">
                              <p>Current Photos:</p>
                              <div className="d-flex flex-wrap gap-2">
                                {formData.dealPhoto.map((photo, index) => (
                                  <div key={index} className="position-relative" style={{ width: '100px' }}>
                                    <img
                                      src={photo.url}
                                      alt={`Deal Preview ${index + 1}`}
                                      className="img-thumbnail"
                                      style={{
                                        width: '95%',
                                        height: '95%',
                                        objectFit: 'contain',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd'
                                      }}
                                    />
                                    <Button
                                      color="danger"
                                      size="sm"
                                      className="position-absolute"
                                      style={{
                                        top: '-10px',
                                        right: '0px',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0
                                      }}
                                      onClick={() => handleRemovePhoto(index, photo.id)}
                                    >
                                      ×
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
  <Col md={6}>
    <FormGroup>
      <Label>
        Start Time <span className="text-danger">*</span>
      </Label>
      <TimePicker
        showSecond={false}
        use12Hours
        format="h:mm A"
        placeholder="Select Start Time"
        value={formData.startTime ? moment(formData.startTime, "HH:mm") : null}
        onChange={(value) => {
          setFormData((prev) => ({
            ...prev,
            startTime: value ? value.format("HH:mm") : "",
          }));
        }}
        className="w-100"
        inputReadOnly
        popupClassName="timepicker-dropdown"
      />
    </FormGroup>
  </Col>

  <Col md={6}>
    <FormGroup>
      <Label>
        End Time <span className="text-danger">*</span>
      </Label>
      <TimePicker
        showSecond={false}
        use12Hours
        format="h:mm A"
        placeholder="Select End Time"
        value={formData.endTime ? moment(formData.endTime, "HH:mm") : null}
        onChange={(value) => {
          setFormData((prev) => ({
            ...prev,
            endTime: value ? value.format("HH:mm") : "",
          }));
        }}
        className="w-100"
        inputReadOnly
        popupClassName="timepicker-dropdown"
      />
    </FormGroup>
  </Col>
</Row>

                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>Days <span className="text-danger">*</span></Label>
                          <div className="d-flex flex-wrap gap-2">
                            {daysOptions.map(day => (
                              <div key={day} className="form-check">
                                <Input
                                  type="checkbox"
                                  id={`day-${day}`}
                                  checked={formData.days.includes(day)}
                                  onChange={() => handleDayChange(day)}
                                  disabled={
                                    isFreePlan &&
                                    formData.days.length >= 5 &&
                                    !formData.days.includes(day)
                                  }
                                />
                                <Label for={`day-${day}`} className="form-check-label">
                                  {day}
                                </Label>
                              </div>
                            ))}
                          </div>
                          {formData.days.length === 0 && (
                            <small className="text-danger">Please select at least one day</small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={loading || categoriesLoading || dealLoading}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Updating...
                          </>
                        ) : 'Update Happy Hour'}
                      </Button>
                    </div>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        {showCropper && (
          <div className="crop-modal">
            <div className="crop-modal-content">
              <h4>Crop your deal photo</h4>
              <div className="crop-container">
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={2.5 / 3}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="rect"
                  zoomWithWheel={true}
                  minZoom={0.5}
                  maxZoom={3}
                  wheelSpeed={0.01}
                />
              </div>
              <div className="crop-controls">
                <input
                  type="range"
                  value={zoom}
                  min={0.5}
                  max={3}
                  step={0.01}
                  onChange={(e) => setZoom(e.target.value)}
                />
                <div className="crop-buttons">
                  <Button
                    color="secondary"
                    onClick={() => {
                      setShowCropper(false);
                      setSelectedFileName('No file chosen');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button color="primary" onClick={handleCropComplete}>
                    Save Crop
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

export default UpdateHH;